import { api } from './client';

// ─── Types ───
export interface ResearchReport {
  id: number;
  stockCode: string;
  stockName: string;
  nameEn?: string;
  industry?: string;
  market: string;
  rating: string;
  targetPrice: number;
  currentPrice: number;
  upside: number;
  summary: string;
  status: string;
  progress: number;
  stage: string;
  failReason?: string;
  detail?: ResearchDetail;
  createdAt: number;
}

export interface ResearchDetail {
  rating: string;
  targetPrice: number;
  summary: string[];
  reportDate: string;
  dataCutoff: string;
  business: {
    segments: { name: string; value: number; color: string }[];
    industryPosition: string;
    industryOutlook: string;
  };
  financials: {
    years: string[];
    revenue: number[];
    netProfit: number[];
    grossMargin: number[];
    roe: number[];
    operatingCashFlow: number[];
  };
  forecast: {
    years: string[];
    revenue: number[];
    netProfit: number[];
    ebitda: number[];
  };
  dcf: {
    wacc: number;
    perpetuityGrowth: number;
    forecastYears: number;
    intrinsicValue: number;
    sensitivity: {
      waccValues: number[];
      growthValues: number[];
      matrix: number[][];
    };
  };
  relativeValuation: {
    peCurrent: number;
    peHistoricalP50: number;
    pePeerMedian: number;
    pbCurrent: number;
    pbHistoricalP50: number;
    pbPeerMedian: number;
    evEbitdaCurrent: number;
    evEbitdaPeerMedian: number;
    peers: { name: string; revenueGrowth: number; grossMargin: number; pe: number; pb: number; evEbitda: number }[];
  };
  thesis: { bullish: string[]; bearish: string[] };
  risks: string[];
  radarData: {
    indicators: { subject: string; fullMark: number }[];
    peers: { name: string; data: number[] }[];
  };
}

// ─── API Calls ───

/** 提交研报生成请求 */
export const createResearchReport = (code: string, market?: string, name?: string, industry?: string, preference?: string) =>
  api.post<ResearchReport>('/research/reports', { code, market, name, industry, preference });

/** 获取研报列表 — 不传 pageSize = 不限制(9999)，展示全部研报 */
export const listResearchReports = (params?: { limit?: number; status?: string }) => {
  const q = new URLSearchParams();
  // 后端 getPagination() 识别 pageSize/page_size/size, 不识别 limit.
  // 用一个大数取消默认 10 条的限制。
  q.set('pageSize', String(params?.limit ?? 9999));
  if (params?.status) q.set('status', params.status);
  const qs = q.toString();
  // 后端用 paginatedResponse 返回，字段是 data.list
  return api.get<{ list: ResearchReport[] }>(`/research/reports${qs ? '?' + qs : ''}`);
};

/** 获取研报详情（含 detail JSON） */
export const getResearchReport = (id: number) =>
  api.get<ResearchReport>(`/research/reports/${id}`);

/** 删除研报 */
export const deleteResearchReport = (id: number) =>
  api.delete(`/research/reports/${id}`);

/** 获取生成进度 */
export const getResearchProgress = (id: number) =>
  api.get<{ progress: number; stage: string; status: string }>(`/research/reports/${id}/progress`);

/** DCF 历史 */
export const getDcfHistory = (id: number) =>
  api.get<any[]>(`/research/reports/${id}/dcf-history`);

export const saveDcfHistory = (id: number, params: { wacc: number; growth: number; intrinsicValue: number }) =>
  api.post(`/research/reports/${id}/dcf-history`, params);

/** 研报自选 */
export const listResearchWatchlist = () =>
  api.get<{ list: any[] }>('/research/watchlist');

export const addResearchWatchlist = (code: string) =>
  api.post('/research/watchlist', { code });

export const removeResearchWatchlist = (code: string) =>
  api.delete(`/research/watchlist/${code}`);

/** 股票搜索 */
export const searchResearchStocks = (q: string) =>
  api.get<{ list: any[] }>(`/research/stocks/search?q=${encodeURIComponent(q)}`);

/** 用户设置 */
export const getResearchSettings = () =>
  api.get<any>('/research/settings');

export const saveResearchSettings = (settings: any) =>
  api.put('/research/settings', settings);

export interface PipelineItem {
  label: string;
  value: string;
  healthy: boolean;
  hint: string;
}

export interface PipelineGroup {
  label: string;
  healthy: boolean;
  items: PipelineItem[];
}

/** 获取行情、回测、AI 与研报流水线的健康状态。 */
export const getPipelineStatus = () =>
  api.get<{ groups: PipelineGroup[] }>('/research/pipeline-status');
